import React from 'react';
import { Box } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import { IAttachment } from '../../types';
import { classes } from '../../../Components/Modals/styles';
import { useDragScroll } from '../../../hooks';
import { ReactDOMAttributes } from '@use-gesture/react/dist/declarations/src/types';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export default function PdfPreview({
  onPinchHandler,
  selectedAttachment,
  setIsLoadingPreview,
  zoom,
  assetUrl,
  onDocumentLoad,
  isZoomReady
}: {
  onPinchHandler: (...args: unknown[]) => ReactDOMAttributes;
  selectedAttachment: IAttachment;
  setIsLoadingPreview: React.Dispatch<React.SetStateAction<boolean>>;
  zoom: number;
  assetUrl?: string;
  onDocumentLoad?: (contentWidth: number) => void;
  isZoomReady?: boolean;
}) {
  const BASE_URL = window.location.origin;
  const [scrollRef] = useDragScroll();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const scaledDimension = zoom < 1 ? '100%' : `${100 / zoom}%`;
  const pdfUrl = assetUrl || `${BASE_URL}/admin/assets/${selectedAttachment.id}`;

  const [numPages, setNumPages] = React.useState<number>(0);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setIsLoadingPreview(false);
    if (onDocumentLoad) {
      const pdfWidth = 750;
      setTimeout(() => {
        onDocumentLoad(pdfWidth);
      }, 300);
    }
  }

  function onDocumentLoadProgress({ loaded, total }: { loaded: number; total: number }) {
    if (loaded >= total) {
      setIsLoadingPreview(false);
    }
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        overflow: 'hidden',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-x pan-y pinch-zoom',
        width: '100%',
        height: '100%',
        maxHeight: '100%',
        '&[data-ready="true"]': {
          overflow: 'auto'
        }
      }}
      data-ready={numPages > 0 ? 'true' : 'false'}
    >
      <Box
        ref={scrollRef}
        sx={{
          paddingBottom: '5px',
          transform: `scale(${zoom})`,
          transformOrigin: 'top left',
          width: zoom < 1 ? '750px' : scaledDimension,
          height: zoom < 1 ? 'auto' : scaledDimension,
          minWidth: zoom < 1 ? '750px' : undefined,
          visibility: isZoomReady === false && zoom >= 1 ? 'hidden' : 'visible'
        }}
        {...onPinchHandler()}
      >
        <Document
          file={pdfUrl}
          loading={<Box sx={classes.previewLoader}></Box>}
          onLoadStart={() => setIsLoadingPreview(true)}
          onLoadError={() => setIsLoadingPreview(false)}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadProgress={onDocumentLoadProgress}
        >
          {[...Array(numPages)].map((_, page) => (
            <Box sx={{ ...classes.pdfPage }} key={page}>
              <Page
                width={750}
                pageNumber={page + 1}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Box>
          ))}
        </Document>
      </Box>
    </Box>
  );
}
